import React, { useState } from 'react'
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Input,
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Chip,
  Progress,
  Spinner,
  Pagination,
  Select,
  SelectItem,
} from '@heroui/react'

import { useToast } from '../../../hooks'
import {
  getGifDocumentsStatus,
  batchConvertMp4sToGif,
  formatFileSize,
  formatUploadDate,
  type BatchConversionResult,
} from '../services/mediaManagementService'

const PlayIcon = () => <span>▶️</span>
const CheckIcon = () => <span>✅</span>
const ScanIcon = () => <span>🔍</span>

interface ScanState {
  isScanning: boolean
  scannedCount: number
  totalCount: number
  foundMissing: number
  canCancel: boolean
}

interface ConversionProgress {
  isRunning: boolean
  currentBatch: string[]
  results: BatchConversionResult | null
  error: string | null
}

interface GifData {
  items: Array<{
    gifId: string
    fileName: string
    fileSizeBytes: number
    uploadedAt: string
    canRepair: boolean
  }>
  totalCount: number
  hasMore: boolean
  nextOffset: number
}

interface GifManagementProps {
  onShowProgress: (type: 'conversion', progress: ConversionProgress) => void
}

export const GifManagement: React.FC<GifManagementProps> = ({
  onShowProgress,
}) => {
  const [gifData, setGifData] = useState<GifData | null>(null)
  const [scanState, setScanState] = useState<ScanState>({
    isScanning: false,
    scannedCount: 0,
    totalCount: 0,
    foundMissing: 0,
    canCancel: false,
  })
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [conversionProgress, setConversionProgress] =
    useState<ConversionProgress>({
      isRunning: false,
      currentBatch: [],
      results: null,
      error: null,
    })

  const toast = useToast()

  const scanForMissingGifs = async () => {
    setScanState({
      isScanning: true,
      scannedCount: 0,
      totalCount: 0,
      foundMissing: 0,
      canCancel: true,
    })
    try {
      let allMissingGif: Array<{
        gifId: string
        fileName: string
        fileSizeBytes: number
        uploadedAt: string
        canRepair: boolean
      }> = []
      let offset = 0
      let hasMore = true
      let total = 0
      let scanned = 0

      while (hasMore) {
        const status = await getGifDocumentsStatus({ offset, limit: 200 })
        allMissingGif = [...allMissingGif, ...status.missingGifList]
        total = status.totalGifDocs
        scanned += 200
        hasMore = status.hasMore || false
        offset = status.nextOffset || 0

        setScanState((prev) => ({
          ...prev,
          scannedCount: Math.min(scanned, total),
          totalCount: total,
          foundMissing: allMissingGif.length,
        }))

        if (hasMore) {
          toast.success(
            `Scanned ${Math.min(scanned, total)} docs, found ${allMissingGif.length} with missing GIF files...`,
            { duration: 1000 }
          )
        }
      }

      setGifData({
        items: allMissingGif,
        totalCount: allMissingGif.length,
        hasMore: false,
        nextOffset: 0,
      })
      setPage(1)
      setSelectedIds(new Set())
      toast.success(
        `Scan complete! Found ${allMissingGif.length} documents with missing GIF files out of ${total} total`
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to scan for missing GIFs'
      toast.error(errorMessage)
    } finally {
      setScanState((prev) => ({ ...prev, isScanning: false, canCancel: false }))
    }
  }

  const handleSelectAll = (checked: boolean) => {
    const currentPageItems = getPaginatedItems()
    if (checked) {
      const set = new Set(selectedIds)
      currentPageItems
        .filter((it) => it.canRepair)
        .forEach((it) => set.add(it.gifId))
      setSelectedIds(set)
    } else {
      const set = new Set(selectedIds)
      currentPageItems.forEach((it) => set.delete(it.gifId))
      setSelectedIds(set)
    }
  }

  const handleSelect = (gifId: string, checked: boolean) => {
    const set = new Set(selectedIds)
    if (checked) set.add(gifId)
    else set.delete(gifId)
    setSelectedIds(set)
  }

  const getFilteredItems = () => {
    if (!gifData) return []
    return gifData.items.filter(
      (it) =>
        it.gifId.toLowerCase().includes(searchTerm.toLowerCase()) ||
        it.fileName.toLowerCase().includes(searchTerm.toLowerCase())
    )
  }

  const getPaginatedItems = () => {
    const filtered = getFilteredItems()
    const startIndex = (page - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filtered.slice(startIndex, endIndex)
  }

  const getTotalPages = () => {
    const filtered = getFilteredItems()
    return Math.ceil(filtered.length / pageSize)
  }

  const handleBatchConvert = async () => {
    if (selectedIds.size === 0) return
    const gifIds = Array.from(selectedIds)
    const progress: ConversionProgress = {
      isRunning: true,
      currentBatch: gifIds,
      results: null,
      error: null,
    }

    setConversionProgress(progress)
    onShowProgress('conversion', progress)

    try {
      const result = await batchConvertMp4sToGif(gifIds)
      const updated = { ...progress, isRunning: false, results: result }
      setConversionProgress(updated)
      onShowProgress('conversion', updated)

      const successfulIds = result.results
        .filter((r) => r.success)
        .map((r) => r.gifId)
      if (gifData) {
        setGifData({
          ...gifData,
          items: gifData.items.filter(
            (item) => !successfulIds.includes(item.gifId)
          ),
          totalCount: gifData.totalCount - successfulIds.length,
        })
      }

      setSelectedIds(new Set())
      toast.success(
        `Successfully repaired ${result.successful} GIF files from MP4s!`
      )
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Conversion failed'
      const updated = { ...progress, isRunning: false, error: errorMessage }
      setConversionProgress(updated)
      onShowProgress('conversion', updated)
      toast.error(errorMessage)
    }
  }

  const handleScanAndConvertAll = async () => {
    await scanForMissingGifs()
    setTimeout(() => {
      if (gifData && gifData.items.length > 0) {
        setSelectedIds(new Set(gifData.items.map((i) => i.gifId)))
        handleBatchConvert()
      }
    }, 1000)
  }

  const currentItems = getPaginatedItems()
  const allSelected =
    currentItems.length > 0 &&
    currentItems
      .filter((it) => it.canRepair)
      .every((it) => selectedIds.has(it.gifId))
  const someSelected = currentItems
    .filter((it) => it.canRepair)
    .some((it) => selectedIds.has(it.gifId))

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between w-full">
            <div>
              <h3 className="text-lg font-semibold">GIF Document Validation</h3>
              <p className="text-slate-600 dark:text-slate-400">
                Scan Firestore documents to find missing GIF files in storage
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                onPress={scanForMissingGifs}
                isDisabled={scanState.isScanning}
                color="primary"
                variant="flat"
                startContent={
                  scanState.isScanning ? <Spinner size="sm" /> : <ScanIcon />
                }
              >
                {scanState.isScanning ? 'Scanning...' : 'Scan GIF Documents'}
              </Button>
              {gifData && gifData.totalCount > 0 && (
                <Button
                  onPress={handleScanAndConvertAll}
                  isDisabled={
                    scanState.isScanning || conversionProgress.isRunning
                  }
                  color="success"
                  startContent={<PlayIcon />}
                >
                  Scan & Repair All
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {scanState.isScanning && (
          <CardBody>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm">
                  Scanned {scanState.scannedCount} of{' '}
                  {scanState.totalCount || '?'} docs
                </span>
                <span className="text-sm font-medium">
                  Found {scanState.foundMissing} documents with missing GIF
                  files
                </span>
              </div>
              <Progress
                value={
                  scanState.totalCount > 0
                    ? (scanState.scannedCount / scanState.totalCount) * 100
                    : 0
                }
                className="w-full"
                color="primary"
              />
            </div>
          </CardBody>
        )}
      </Card>

      {gifData && gifData.totalCount > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between w-full">
              <div>
                <h3 className="text-lg font-semibold">
                  Documents Missing GIF Files ({gifData.totalCount})
                </h3>
                <p className="text-slate-600 dark:text-slate-400">
                  Documents that can be repaired have MP4 files available for
                  conversion
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <Chip variant="bordered" size="sm">
                  {selectedIds.size} selected
                </Chip>
                <Button
                  onPress={handleBatchConvert}
                  isDisabled={
                    selectedIds.size === 0 || conversionProgress.isRunning
                  }
                  color="success"
                  startContent={<PlayIcon />}
                >
                  Repair Selected
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody className="space-y-4">
            <div className="flex items-center justify-between">
              <Input
                placeholder="Search by document ID or filename..."
                value={searchTerm}
                onValueChange={setSearchTerm}
                className="max-w-sm"
              />
              <Select
                label="Per page"
                value={pageSize.toString()}
                onChange={(e) => {
                  setPageSize(Number(e.target.value))
                  setPage(1)
                }}
                className="w-32"
                size="sm"
              >
                <SelectItem key="25">25</SelectItem>
                <SelectItem key="50">50</SelectItem>
                <SelectItem key="100">100</SelectItem>
              </Select>
            </div>

            <Table>
              <TableHeader>
                <TableColumn className="w-12">
                  <Checkbox
                    isSelected={allSelected}
                    onValueChange={handleSelectAll}
                    isIndeterminate={someSelected && !allSelected}
                  />
                </TableColumn>
                <TableColumn>Document ID</TableColumn>
                <TableColumn>Filename</TableColumn>
                <TableColumn>Status</TableColumn>
                <TableColumn>Size</TableColumn>
                <TableColumn>Uploaded</TableColumn>
              </TableHeader>
              <TableBody>
                {currentItems.map((it) => (
                  <TableRow key={it.gifId}>
                    <TableCell>
                      <Checkbox
                        isSelected={selectedIds.has(it.gifId)}
                        onValueChange={(checked) =>
                          handleSelect(it.gifId, checked)
                        }
                        isDisabled={!it.canRepair}
                      />
                    </TableCell>
                    <TableCell className="font-mono text-sm">
                      {it.gifId}
                    </TableCell>
                    <TableCell>{it.fileName}</TableCell>
                    <TableCell>
                      {it.canRepair ? (
                        <Chip color="warning" variant="flat" size="sm">
                          Can Repair (MP4 available)
                        </Chip>
                      ) : (
                        <Chip color="danger" variant="flat" size="sm">
                          Needs Manual Fix (No MP4)
                        </Chip>
                      )}
                    </TableCell>
                    <TableCell>{formatFileSize(it.fileSizeBytes)}</TableCell>
                    <TableCell>{formatUploadDate(it.uploadedAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {getTotalPages() > 1 && (
              <div className="flex justify-center">
                <Pagination
                  total={getTotalPages()}
                  page={page}
                  onChange={setPage}
                  showControls
                  showShadow
                />
              </div>
            )}

            {getFilteredItems().length === 0 && searchTerm && (
              <div className="text-center py-4 text-slate-600">
                No documents found matching &quot;{searchTerm}&quot;
              </div>
            )}
          </CardBody>
        </Card>
      )}

      {gifData && gifData.totalCount === 0 && !scanState.isScanning && (
        <Card>
          <CardBody className="pt-6">
            <div className="text-center">
              <div className="text-6xl mb-4">
                <CheckIcon />
              </div>
              <h3 className="text-lg font-medium mb-2">
                All documents have their GIF files!
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Every GIF document in Firestore has a corresponding file in
                storage.
              </p>
            </div>
          </CardBody>
        </Card>
      )}
    </div>
  )
}

export default GifManagement
